import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Zap, Car, Sun, Battery, Wifi, ShieldCheck,
  Sparkles, Cpu, Activity, Award, Send, CheckCircle2, BadgeCheck,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";

const HERO_IMG = "/images/zaptec-hero.png";

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section className="relative pt-32 pb-20 overflow-hidden" data-testid="zaptec-hero">
    <div className="blob-pink absolute -top-20 -right-20 opacity-60" />
    <div className="blob-pink absolute top-1/3 -left-32 opacity-40" />

    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-6 z-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-soft border border-brand/20 mb-7">
          <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-deep">Zaptec Premium Partner</span>
        </div>

        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-navy-900 leading-[1.02] text-balance" data-testid="zaptec-hero-title">
          Slim laden met <span className="gradient-pink-text">Zaptec.</span>
        </h1>

        <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-xl">
          Laad slimmer, sneller en voordeliger met een premium Zaptec laadpaal, inclusief professionele installatie, configuratie en ingebruikname.
        </p>

        <div className="mt-9 flex flex-col sm:flex-row gap-4">
          <a href="#zaptec-quote" className="btn-pink" data-testid="zaptec-hero-cta-quote">
            Vraag offerte aan <ArrowRight size={18} />
          </a>
          <a href="#zaptec-products" className="btn-ghost" data-testid="zaptec-hero-cta-products">
            Bekijk prijzen
          </a>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-5 max-w-md">
          <Stat tag="Vermogen" value="22 kW" sub="max snelheid" />
          <Stat tag="Garantie" value="5 jaar" sub="fabrieksgarantie" />
          <Stat tag="Installatie" value="1 dag" sub="sleutelklaar" />
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="lg:col-span-6 relative z-10">
        <div className="relative rounded-3xl overflow-hidden aspect-[4/3] lg:aspect-[5/4] shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
          <img src={HERO_IMG} alt="Zaptec laadpaal aan moderne muur, slim laden voor elektrische auto" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-tr from-navy-900/30 via-transparent to-transparent" />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="absolute bottom-5 left-5 right-5 sm:bottom-7 sm:left-7 sm:right-auto glass rounded-2xl p-5 sm:max-w-xs"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl gradient-pink text-white flex items-center justify-center">
                <Zap size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold">Live</div>
                <div className="font-heading font-bold text-navy-900">Laden op zonne-energie</div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Stat = ({ tag, value, sub }) => (
  <div className="border-l-2 border-brand pl-3">
    <div className="text-[10px] uppercase tracking-widest text-brand-deep font-bold">{tag}</div>
    <div className="font-heading text-2xl font-black text-navy-900 leading-tight">{value}</div>
    <div className="text-[11px] text-slate-500">{sub}</div>
  </div>
);

/* ---------------- PRODUCTS ---------------- */
const scrollToQuote = (model) => {
  const el = document.getElementById("zaptec-quote");
  if (!el) return;
  if (model) {
    try { window.sessionStorage.setItem("zaptec_interest", model); } catch (e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent("zaptec:interest", { detail: model }));
  }
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  // Focus first input after scroll for accessibility
  setTimeout(() => {
    const firstInput = document.querySelector('[data-testid="zq-name"]');
    if (firstInput) firstInput.focus({ preventScroll: true });
  }, 650);
};

const Products = () => {
  const items = [
    {
      name: "Zaptec Go",
      tag: "Compact",
      desc: "Het bekroonde Scandinavische ontwerp, krachtig, compact en eenvoudig in gebruik. Ideaal voor de meeste woningen.",
      bullets: ["Tot 22 kW (3-fase)", "Geïntegreerde aardlekbeveiliging type B", "Bediening via Zaptec app", "WiFi-verbinding"],
    },
    {
      name: "Zaptec Go 2",
      tag: "Nieuwste model",
      desc: "Het next-gen model met 4G, dynamic load balancing en geavanceerde slim-laden functies voor zonnepanelen en dynamische tarieven.",
      bullets: ["Tot 22 kW (3-fase)", "WiFi + 4G ingebouwd", "Smart-charging met zonnepanelen", "Geschikt voor dynamische contracten"],
    },
  ];
  const options = [
    { name: "Wandmontage", desc: "Inclusief installatie", price: "€ 1.750" },
    { name: "Montage op paal", desc: "Inclusief installatie", price: "€ 2.250" },
  ];

  return (
    <section id="zaptec-products" className="section-pad bg-slate-50/70" data-testid="zaptec-products">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Onze laadpalen</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Premium Zaptec laadpalen met installatie.</h2>
          <p className="mt-5 text-slate-600 text-lg">Twee modellen, één bewezen kwaliteit. Wij adviseren u welk model en welke installatie het beste past.</p>
        </div>

        {/* Models */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {items.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              role="button"
              tabIndex={0}
              onClick={() => scrollToQuote(p.name)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  scrollToQuote(p.name);
                }
              }}
              aria-label={`Vraag offerte aan voor ${p.name}`}
              className="relative cursor-pointer bg-white rounded-3xl border border-slate-200 p-7 lg:p-8 hover:border-brand/40 hover:shadow-[0_30px_80px_rgba(255,47,125,0.18)] hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 active:translate-y-0 transition-all duration-500 group overflow-hidden flex flex-col"
              data-testid={`zaptec-model-${i}`}
            >
              {/* hover glow accent */}
              <div className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-brand/5 via-transparent to-transparent" />

              <div className="relative flex items-center justify-between mb-4">
                <span className="inline-flex px-3 py-1 rounded-full bg-brand-soft text-brand-deep text-xs font-bold tracking-wide">{p.tag}</span>
                <div className="w-11 h-11 rounded-xl gradient-pink text-white flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                  <Zap size={20} />
                </div>
              </div>
              <h3 className="relative font-heading text-3xl font-black text-navy-900 group-hover:text-brand-deep transition-colors duration-300">{p.name}</h3>
              <p className="relative mt-3 text-slate-600 leading-relaxed">{p.desc}</p>
              <ul className="relative mt-5 space-y-2">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full bg-brand-soft text-brand flex items-center justify-center flex-shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              {/* CTA at the bottom */}
              <div className="relative mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-navy-900 group-hover:text-brand transition-colors duration-300">
                  Vraag offerte aan
                </span>
                <span className="w-10 h-10 rounded-full bg-brand-soft text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white group-hover:translate-x-1 transition-all duration-300" aria-hidden="true">
                  <ArrowRight size={18} />
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Installation prices */}
        <div className="grid md:grid-cols-2 gap-6">
          {options.map((o, i) => (
            <motion.div
              key={o.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-white rounded-3xl border border-brand/20 p-7 flex items-center justify-between gap-5 hover:border-brand/40 transition-all"
              data-testid={`zaptec-install-${i}`}
            >
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold mb-1">{o.name}</div>
                <div className="font-heading font-black text-xl text-navy-900">{o.desc}</div>
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl font-black gradient-pink-text">{o.price}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-7 bg-white border border-slate-200 rounded-2xl p-5 lg:p-6 flex items-start gap-4">
          <BadgeCheck className="text-brand flex-shrink-0 mt-0.5" size={22} />
          <div className="text-sm text-slate-600 leading-relaxed">
            <strong className="text-navy-900">Alles inbegrepen:</strong> professionele installatie, configuratie & ingebruikname. Prijzen kunnen afwijken afhankelijk van uw situatie en meterkast, bij graafwerk of uitgebreide aanpassingen geldt een meerprijs. Wij maken vooraf altijd een transparante offerte op maat.
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- USPs ---------------- */
const USPs = () => {
  const items = [
    { icon: Zap, title: "Tot 22 kW", desc: "Snelladen op 3-fase, uw EV is in enkele uren vol." },
    { icon: Car, title: "Werkt met elke EV", desc: "Compatibel met vrijwel iedere elektrische auto op de markt." },
    { icon: Award, title: "Bekroond design", desc: "Compact Scandinavisch ontwerp, winnaar Red Dot Design Award." },
    { icon: Cpu, title: "Slim laden via app", desc: "Volledige controle vanaf uw smartphone, overal en altijd." },
    { icon: Sun, title: "Zonne-energie ready", desc: "Laad automatisch wanneer uw zonnepanelen overschot leveren." },
    { icon: Activity, title: "Dynamische tarieven", desc: "Werkt met Tibber, EnergyZero en andere uur-tarief contracten." },
    { icon: ShieldCheck, title: "5 jaar garantie", desc: "Op de laadpaal én op de installatie door Helios Bright." },
    { icon: Sparkles, title: "Toekomstbestendig", desc: "OCPP-compatibel, klaar voor bidirectioneel laden (V2H/V2G)." },
  ];
  return (
    <section className="section-pad" data-testid="zaptec-usps">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Waarom Zaptec</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Het slimste laadsysteem voor uw woning.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="bg-white border border-slate-200 rounded-3xl p-6 hover:border-brand/40 hover:-translate-y-1 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center mb-4">
                <it.icon size={20} />
              </div>
              <h3 className="font-heading font-bold text-navy-900 mb-1">{it.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- SMART CHARGING ---------------- */
const SmartCharging = () => {
  const items = [
    { icon: Sun, title: "Laad met uw zonnepanelen", desc: "Wanneer uw panelen overschot opwekken, laadt Zaptec automatisch uw auto. Volledig gratis kilometers." },
    { icon: Activity, title: "Laden op goedkope uren", desc: "Zaptec leest de actuele uurtarieven uit en laadt automatisch wanneer stroom het goedkoopst is." },
    { icon: Battery, title: "Slim met thuisbatterij", desc: "Combineer uw Zaptec met een thuisbatterij voor maximale onafhankelijkheid en optimale benutting." },
    { icon: Cpu, title: "Dynamic load balancing", desc: "De laadpaal stemt zich automatisch af op uw huishouden, geen overbelaste meterkast." },
  ];
  return (
    <section className="section-pad bg-navy-900 text-white relative overflow-hidden" data-testid="zaptec-smart">
      <div className="blob-pink absolute -top-32 -right-20 opacity-40" />
      <div className="absolute -bottom-20 left-0 w-[400px] h-[400px] rounded-full bg-emerald-500/15 blur-[100px]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Slim laden</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-balance">Niet alleen laden. <span className="gradient-pink-text">Slim laden.</span></h2>
          </div>
          <p className="text-slate-300 text-lg">Zaptec werkt naadloos samen met zonnepanelen, thuisbatterijen en dynamische energiecontracten, voor de laagst mogelijke laadkosten en maximale eigen energie.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 hover:border-brand/40 transition-all"
            >
              <div className="w-11 h-11 rounded-xl gradient-pink flex items-center justify-center mb-4">
                <it.icon size={20} />
              </div>
              <h3 className="font-heading font-bold mb-2">{it.title}</h3>
              <p className="text-sm text-slate-300 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- QUOTE FORM ---------------- */
const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", postcode: "",
    car_type: "", has_solar: "", has_battery: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [interest, setInterest] = useState("");

  React.useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem("zaptec_interest");
      if (stored) setInterest(stored);
    } catch (e) { /* ignore */ }
    const handler = (e) => setInterest(e.detail || "");
    window.addEventListener("zaptec:interest", handler);
    return () => window.removeEventListener("zaptec:interest", handler);
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const extras = [
        interest ? `Interesse in model: ${interest}` : "",
        form.car_type ? `Type auto: ${form.car_type}` : "",
        form.has_solar ? `Zonnepanelen: ${form.has_solar}` : "",
        form.has_battery ? `Thuisbatterij: ${form.has_battery}` : "",
        form.message ? `Bericht: ${form.message}` : "",
      ].filter(Boolean).join("\n");

      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        postcode: form.postcode,
        message: extras,
        source: "zaptec_quote",
      });
      setDone(true);
      toast.success("Bedankt! Wij nemen binnen 24 uur contact op.");
    } catch (err) {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section id="zaptec-quote" className="section-pad" data-testid="zaptec-quote-success">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <div className="bg-white rounded-3xl border border-brand/30 p-10 text-center shadow-[0_30px_80px_rgba(255,47,125,0.12)]">
            <div className="w-16 h-16 rounded-full bg-brand-soft mx-auto flex items-center justify-center mb-5">
              <CheckCircle2 className="text-brand" size={32} />
            </div>
            <h3 className="font-heading text-3xl font-black text-navy-900 mb-2">Aanvraag ontvangen</h3>
            <p className="text-slate-600">U ontvangt binnen 24 uur een persoonlijke offerte op het opgegeven e-mailadres.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="zaptec-quote" className="section-pad bg-slate-50/70" data-testid="zaptec-quote">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Offerteaanvraag</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Ontvang binnen 24 uur een persoonlijke offerte.</h2>
          <p className="mt-5 text-slate-600 text-lg">Vul uw gegevens in, onze adviseur belt u terug met een offerte op maat. Vrijblijvend en kosteloos.</p>
          {interest && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-soft border border-brand/20" data-testid="zaptec-quote-interest">
              <BadgeCheck size={16} className="text-brand" />
              <span className="text-sm font-semibold text-brand-deep">Interesse: {interest}</span>
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="bg-white rounded-3xl border border-slate-200 p-7 lg:p-10 shadow-[0_30px_80px_rgba(15,23,42,0.05)] space-y-5" data-testid="zaptec-quote-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Naam" required>
              <Input type="text" name="name" required value={form.name} onChange={onChange} className="rounded-xl h-12 border-slate-200" data-testid="zq-name" />
            </Field>
            <Field label="Telefoonnummer" required>
              <Input type="tel" name="phone" required value={form.phone} onChange={onChange} className="rounded-xl h-12 border-slate-200" data-testid="zq-phone" />
            </Field>
            <Field label="E-mailadres" required>
              <Input type="email" name="email" required value={form.email} onChange={onChange} className="rounded-xl h-12 border-slate-200" data-testid="zq-email" />
            </Field>
            <Field label="Postcode">
              <Input type="text" name="postcode" value={form.postcode} onChange={onChange} className="rounded-xl h-12 border-slate-200" data-testid="zq-postcode" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Heeft u zonnepanelen?">
              <Select value={form.has_solar} onValueChange={(v) => setForm((f) => ({ ...f, has_solar: v }))}>
                <SelectTrigger className="rounded-xl h-12 border-slate-200" data-testid="zq-solar"><SelectValue placeholder="Maak een keuze" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ja">Ja</SelectItem>
                  <SelectItem value="Nee">Nee</SelectItem>
                  <SelectItem value="Overweeg ik">Overweeg ik</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Heeft u een thuisbatterij?">
              <Select value={form.has_battery} onValueChange={(v) => setForm((f) => ({ ...f, has_battery: v }))}>
                <SelectTrigger className="rounded-xl h-12 border-slate-200" data-testid="zq-battery"><SelectValue placeholder="Maak een keuze" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ja">Ja</SelectItem>
                  <SelectItem value="Nee">Nee</SelectItem>
                  <SelectItem value="Overweeg ik">Overweeg ik</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Type auto (merk + model)">
            <Input type="text" name="car_type" value={form.car_type} onChange={onChange} placeholder="Bijv. Tesla Model Y, Volvo EX30, BMW iX1" className="rounded-xl h-12 border-slate-200" data-testid="zq-car" />
          </Field>

          <Field label="Bericht (optioneel)">
            <Textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Aanvullende vragen of situatie van uw meterkast?" className="rounded-xl border-slate-200" data-testid="zq-message" />
          </Field>

          <button type="submit" disabled={loading} className="btn-pink w-full !py-4 disabled:opacity-60" data-testid="zq-submit">
            {loading ? "Versturen..." : <>Ontvang binnen 24 uur een persoonlijke offerte <Send size={16} /></>}
          </button>
          <p className="text-xs text-slate-500 text-center">Vrijblijvend en kosteloos · Geen verkooppraatjes · 100% AVG-proof</p>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-sm font-semibold text-navy-900 mb-2 block">{label}{required && <span className="text-brand"> *</span>}</label>
    {children}
  </div>
);

/* ---------------- FAQ ---------------- */
const FAQ = () => {
  const faqs = [
    { q: "Wat kost een Zaptec laadpaal?", a: "Een Zaptec Go inclusief professionele wandmontage is € 1.750. Montage op paal kost € 2.250. Beide prijzen zijn inclusief installatie, configuratie en ingebruikname." },
    { q: "Werkt Zaptec met zonnepanelen?", a: "Ja. Zaptec is ontworpen om optimaal samen te werken met zonnepanelen. De laadpaal kan automatisch beslissen om alleen te laden wanneer uw panelen overschot leveren, zo laadt u praktisch gratis." },
    { q: "Hoe snel kan mijn laadpaal geplaatst worden?", a: "In de meeste gevallen kunnen we uw Zaptec binnen 2 tot 4 weken na akkoord installeren. Onze gecertificeerde monteurs leveren de installatie in 1 dag op." },
    { q: "Is Zaptec geschikt voor elke elektrische auto?", a: "Ja. Zaptec werkt met vrijwel elke EV op de markt, Tesla, Volvo, BMW, Audi, Polestar, Hyundai, Kia, Mercedes-Benz en meer. De Type 2 stekker is de standaard in Europa." },
    { q: "Wat kost installatie?", a: "De installatie is volledig inbegrepen in onze pakketprijzen. Bij graafwerk, lange kabels of uitgebreide aanpassingen aan de meterkast kan een meerprijs gelden, dat communiceren we altijd vooraf in een transparante offerte." },
    { q: "Kan ik slim laden met dynamische stroomprijzen?", a: "Absoluut. Zaptec werkt naadloos met dynamische energiecontracten (Tibber, EnergyZero, ANWB Energie). De laadpaal laadt automatisch op de goedkoopste uren van de dag." },
  ];
  return (
    <section className="section-pad" data-testid="zaptec-faq">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Veelgestelde vragen</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Antwoorden over Zaptec laadpalen.</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white border border-slate-200 rounded-2xl px-6 data-[state=open]:border-brand/40 data-[state=open]:shadow-[0_10px_30px_rgba(255,47,125,0.08)] transition-all"
              data-testid={`zaptec-faq-${i}`}
            >
              <AccordionTrigger className="font-heading font-bold text-lg text-navy-900 hover:no-underline py-5 text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ---------------- CLOSING ---------------- */
const Closing = () => (
  <section className="py-24" data-testid="zaptec-closing">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="relative rounded-[2.5rem] overflow-hidden bg-navy-900 p-10 lg:p-16 text-white">
        <div className="blob-pink absolute -top-20 -left-20 opacity-60" />
        <div className="blob-pink absolute -bottom-20 -right-20 opacity-40" />
        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-4">Combineer voor maximaal voordeel</p>
            <h3 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-balance">Combineer uw Zaptec met zon & opslag.</h3>
            <p className="text-slate-300 mt-5 text-lg max-w-lg">De grootste besparing bereikt u door Zaptec te combineren met zonnepanelen en/of een thuisbatterij. Onze adviseurs maken één integraal energieplan voor uw woning.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
            <Link to="/zonnepanelen" className="btn-pink !text-white text-center" data-testid="zaptec-closing-solar">
              Zonnepanelen <ArrowRight size={18} />
            </Link>
            <Link to="/thuisbatterijen" className="btn-ghost !bg-white/10 !text-white !border-white/20 hover:!bg-white/20 text-center" data-testid="zaptec-closing-battery">
              Thuisbatterijen
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
const Laadpalen = () => (
  <main data-testid="zaptec-page">
    <Hero />
    <Products />
    <USPs />
    <SmartCharging />
    <QuoteForm />
    <FAQ />
    <Closing />
  </main>
);

export default Laadpalen;
